import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "ui/Scripts/source/framework/base";
import { EditPageHeader } from "../app/header";
import * as instantMessageService from "../../models/services/instantMessageService";
import * as instantMessageChannelService from "../../models/services/instantMessageChannelService";
import { customHistory } from "../../framework/customHistory";
import { IUserState } from "../../models/store/storeTypes";
import { AppUtils } from "../../framework/appUtils";
import { ConfirmationDialogResult, FileType, InstantMessageReadState, UserType } from "ui/Scripts/source/models/enums";
import { SaveDataObj } from "../../framework/saveData";
import { ToolAddFileButton, ToolButton, ToolButtonDropdown } from "ui/Scripts/source/components/framework/toolButton";
import { InstantMessageListLine } from "./instantMessageListLine";
import { RouteComponentProps } from "react-router-dom";
import { CameraPhoto } from "../framework/cameraPhoto";
import { ListLine } from "../framework/listLine";
import { IInstantMessageListItem, InstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IInstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { SignalRConnectionManager } from "ui/Scripts/source/models/signalR/signalRConnectionManager";
import { Session } from "../../models/session/session";
import { ChannelInstantMessageNewAttachment, ChannelInstantMessageNewAttachmentImage } from "ui/Scripts/source/components/instantMessage/instantMessageNewAttachment";
import { StoreUtils } from "../../models/store/storeUtils";
import { SaveAttachmentBase64 } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";

// ChannelInstantMessageNew
export interface IChannelInstantMessageNewProp {
    user: IUserState;
    maxInstantMessageAttachmentSize: number;
    companyId: string;
    channelId: string;
    channelIsReadOnly: boolean;
    replyItem: IInstantMessageListItem;
    onInstantMessageSaved: (item: IInstantMessageListItem) => void;
    onDropAttachments: (e: any) => Promise<void>;
    onDragOverAttachments: (e: any) => void;
    onDragEnterAttachments: (e: any) => void;
    onDragLeaveAttachments: (e: any) => void;
    onInputRowCountChanged: (rowCount: number) => void;
    onReplyContainerSizeChanged: (height: number) => void;
    onRemoveReply: () => void;
    getFileAttachment: (blob: any, category: number) => Promise<FileAttachment>;
}

interface IChannelInstantMessageNewState {
    content: string;
    attachments: FileAttachment[];
    photoAttachmentIds: string[];
    sending: boolean;
    cameraIds: string[];
    showCamera: boolean;
    showingCamera: boolean;
}

export class ChannelInstantMessageNew extends React.Component<IChannelInstantMessageNewProp, IChannelInstantMessageNewState> {
    private inputElement: HTMLTextAreaElement;
    private inputHiddenElement: HTMLTextAreaElement;
    private rowCount = 1;
    private replyContainerDivSizeHash: string;
    private replyContainerDiv: HTMLDivElement;
    private replyContainerResizeObserver: ResizeObserver;

    constructor(props: IChannelInstantMessageNewProp) {
        super(props);
        this.state = {
            content: "",
            attachments: [],
            photoAttachmentIds: [],
            sending: false,
            cameraIds: [],
            showCamera: false,
            showingCamera: false
        };
    }

    updateReployContainerHeight = () => {
        if (!this.replyContainerDiv) return;
        const replyContainerRect = this.replyContainerDiv.getBoundingClientRect();
        this.props.onReplyContainerSizeChanged(replyContainerRect.height);
    };

    updateReployContainerSize = Base.debounce((obj: ChannelInstantMessageNew) => {
        if (!this.replyContainerDiv) return;
        const replyContainerRect = this.replyContainerDiv.getBoundingClientRect();
        const replyContainerDivSizeHash = replyContainerRect.width.toString(10) + "#" + replyContainerRect.height.toString(10);
        if (replyContainerDivSizeHash === obj.replyContainerDivSizeHash) return;
        obj.props.onReplyContainerSizeChanged(replyContainerRect.height);
    }, 25);

    updateReployContainerResizeHandler = () => {
        this.updateReployContainerSize(this);
    };

    componentDidMount(): void {
        Base.getCameraIds().then(ids => {
            this.setState({ cameraIds: ids });
        }).catch(() => {
            this.setState({ cameraIds: [] });
        });
        this.updateReployContainerHeight();
        this.replyContainerResizeObserver = null;
        if (!this.replyContainerDiv) return;
        this.replyContainerResizeObserver = new ResizeObserver(this.updateReployContainerResizeHandler);
        this.replyContainerResizeObserver.observe(this.replyContainerDiv);
    }

    componentDidUpdate(prevProps: IChannelInstantMessageNewProp, prevState: IChannelInstantMessageNewState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevChannelId = prevProps.channelId;
        const channelId = props.channelId;
        if (companyId === prevCompanyId && prevChannelId === channelId) return;
        this.setState({
            content: "",
            attachments: [],
            photoAttachmentIds: []
        });
    }

    getSendingDisabled = (): boolean => {
        const state = this.state;
        return state.sending || !state.content && state.attachments.length < 1;
    };

    getSaveDataFromState = async (props: IChannelInstantMessageNewProp, state: IChannelInstantMessageNewState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("channelId", props.channelId);
        result.append("replyItemId", props.replyItem ? props.replyItem.id : null);
        result.append("content", state.content);
        const attachments: SaveAttachmentBase64[] = [];
        for (const fileAttachment of state.attachments.filter(i => !!i.file)) {
            const attachment = await SaveAttachmentBase64.fromFileAttachment(state.photoAttachmentIds.indexOf(fileAttachment.id) > -1 ? "photo" : "", fileAttachment);
            if (!attachment) continue;
            attachments.push(attachment);
        }
        result.append("attachments", attachments);
        return result;
    };

    updateSavedMessageToList = (id: string) => {
        const props = this.props;
        const state = this.state;
        const newMessage = new InstantMessageListItem();
        newMessage.id = id;
        newMessage.channelId = props.channelId;
        newMessage.content = state.content;
        newMessage.modifyUser = StoreUtils.getNowModifyUser();
        newMessage.attachments = state.attachments.slice(0);
        if (props.replyItem) {
            newMessage.replyToInstantMessage = new InstantMessageListItem(props.replyItem);
        }
        props.onInstantMessageSaved(newMessage);
    };

    updateInputRowCount = (rowCount: number) => {
        const defaultClientHeight = 45;
        const defaultHeight = defaultClientHeight + 2;
        if (this.rowCount !== rowCount) {
            if (rowCount > 1) {
                this.inputElement.style.height = (defaultHeight + ((rowCount - 1) * 24)).toString(10) + "px";
            } else {
                this.inputElement.style.removeProperty("height");
            }
            this.props.onInputRowCountChanged(rowCount);
            this.rowCount = rowCount;
        }
    };

    saveInstantMessage = async () => {
        const props = this.props;
        const state = this.state;
        if (this.getSendingDisabled()) return;
        this.setState({ sending: true });
        try {
            const saveData = await this.getSaveDataFromState(props, state);
            //Call server
            const result = await AppUtils.callService(() => instantMessageService.saveInstantMessage(saveData.obj), false, true);
            if (!result) return null;
            this.updateSavedMessageToList(result.id);
            this.setState({
                content: "",
                attachments: [],
            });
            this.updateInputRowCount(1);
        } finally {
            this.setState({ sending: false });
        }
    };

    handleChange = (event) => {
        this.setState({ content: event.target.value });
    };

    // #region Attachments
    handleAddAttachment = async (fileList: FileList) => {
        const state = this.state;
        const attachments = await FileAttachment.addFileListToFileAttachments(state.attachments, fileList, 0);
        if (attachments.length < 1) return;
        for (const attachment of attachments) {
            if (!AppUtils.validateFileAttachment(attachment, Translations.MaximumSizeOfInstantMessageAttachmentIsParameterMB, this.props.maxInstantMessageAttachmentSize)) return;
        }
        if (!AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };

    handleAddAttachments = (newAttachments: FileAttachment[]) => {
        if (!newAttachments || newAttachments.length < 1) return;
        const attachments = this.state.attachments.slice(0);
        for (const newAttachment of newAttachments) {
            if (!newAttachment) return;
            attachments.push(newAttachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };
    
    handleSetAttachmentDropZone = (value: boolean) => {
        if (!this.inputElement) return;
        if (value) {
            this.inputElement.classList.add("draggingOver");
        } else {
            this.inputElement.classList.remove("draggingOver");
        }
    };
    
    handlePasteAttachments = async (e: any) => {
        //e.preventDefault();
        if (!e.clipboardData.files) {
            return;
        }
        const clipboardItems = e.clipboardData.files;
        const attachments = this.state.attachments.slice(0);
        for (const clipboardItem of clipboardItems) {
            const attachment = await this.props.getFileAttachment(clipboardItem, 0);
            if (!attachment) return;
            attachments.push(attachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        this.setState({
            attachments
        });
    };
    
    handleRemoveAttachment = (id: string) => {
        const state = this.state;
        if (!id) return;
        const attachments = state.attachments.filter(i => i.id !== id);
        const photoAttachmentIds = state.photoAttachmentIds.filter(i => i !== id);
        this.setState({
            attachments,
            photoAttachmentIds
        });
    };
    // #endregion Attachments
    
    // #region Camera
    hasCameras = (): boolean => {
        const state = this.state;
        return state.cameraIds && state.cameraIds.length > 0;
    };

    handleAttachmentAddPhoto = () => {
        if (!this.hasCameras()) return;
        this.setState({ showCamera: true });
    };

    handleCameraStreamAvailable = () => {
        this.setState({ showingCamera: true });
    };

    handleCameraPhotoOk = async (dataUrl: string) => {
        const state = this.state;
        if (Base.isNullOrEmpty(dataUrl)) {
            this.setState({ showCamera: false, showingCamera: false });
            return;
        }
        const attachment = await FileAttachment.photoToFileAttachment(dataUrl, 0);
        if (!attachment) return;
        const attachments = state.attachments.slice(0);
        attachments.push(attachment);
        const photoAttachmentIds = state.photoAttachmentIds.slice(0);
        photoAttachmentIds.push(attachment.id);
        this.setState({ attachments, photoAttachmentIds, showCamera: false, showingCamera: false });
    };

    handleCameraPhotoCancel = (permissionDenied: boolean) => {
        if (!permissionDenied) {
            this.setState({ showCamera: false, showingCamera: false });
        } else {
            this.setState({ showCamera: false, showingCamera: false, cameraIds: [] });
        }
    };
    // #endregion Camera
    
    handleTextAreaInput = (e: any) => {
        this.inputHiddenElement.value = this.inputElement.value;
        this.inputHiddenElement.style.width = (this.inputElement.clientWidth + 2) + "px";
        window.setTimeout(() => {
            const rowCount = Math.min(Math.ceil(this.inputHiddenElement.scrollHeight / this.inputHiddenElement.clientHeight), 3);
            this.updateInputRowCount(rowCount);
        }, 0);
    };

    render() {
        const state = this.state;
        const props = this.props;
        const imageAttachments = state.attachments.filter(i => i.type === FileType.Image);
        const fileAttachments = state.attachments.filter(i => i.type !== FileType.Image);
        const replyUserIsDeleted = props.replyItem ? props.replyItem.modifyUser.userType === UserType.Removed : false;
        const showCamera = this.hasCameras() && state.showCamera;
        if (showCamera) {
            return (
                <CameraPhoto
                    cameraIds={state.cameraIds}
                    cameraStreamAvailable={state.showingCamera}
                    onCameraStreamAvailable={this.handleCameraStreamAvailable}
                    onOkClick={this.handleCameraPhotoOk}
                    onCancelClick={this.handleCameraPhotoCancel}
                />);
        }
        return (
            <div className="instantMessageNew">
                {state.attachments.length > 0 &&
                    <div className="instantMessageNewAttachments">
                        {imageAttachments.length > 0 &&
                            <div className="imAttachmentImages" onDrop={props.onDropAttachments} onDragOver={props.onDragOverAttachments} onDragEnter={props.onDragEnterAttachments} onDragLeave={props.onDragLeaveAttachments}>
                                {imageAttachments.map((attachment) =>
                                    <ChannelInstantMessageNewAttachmentImage
                                        key={attachment.id}
                                        attachment={attachment}
                                        selectedId={""}
                                        onClick={this.handleRemoveAttachment}
                                        onDropAttachments={props.onDropAttachments}
                                        onDragOverAttachments={props.onDragOverAttachments}
                                        onDragEnterAttachments={props.onDragEnterAttachments}
                                        onDragLeaveAttachments={props.onDragLeaveAttachments}
                                    />
                                )}
                            </div>
                        }
                        {fileAttachments.length > 0 &&
                            <div className="imAttachments" onDrop={props.onDropAttachments} onDragOver={props.onDragOverAttachments} onDragEnter={props.onDragEnterAttachments} onDragLeave={props.onDragLeaveAttachments}>
                                {fileAttachments.map((attachment) =>
                                    <ChannelInstantMessageNewAttachment
                                        key={attachment.id}
                                        attachment={attachment}
                                        selectedId={""}
                                        onClick={this.handleRemoveAttachment}
                                        onDropAttachments={props.onDropAttachments}
                                        onDragOverAttachments={props.onDragOverAttachments}
                                        onDragEnterAttachments={props.onDragEnterAttachments}
                                        onDragLeaveAttachments={props.onDragLeaveAttachments}
                                    />
                                )}
                            </div>
                        }
                    </div>
                }
                <div className="replyToContainer" ref={(elem) => { this.replyContainerDiv = elem; }}>
                    {props.replyItem &&
                        <div className={"replyTo" + (state.attachments.length > 0 ? " withAttachment" : "")}>
                            <div className="sender">
                                <span className={replyUserIsDeleted ? "disabled" : ""}>{props.replyItem.modifyUser.name}&nbsp;</span>{Base.timeToTimeStr(props.replyItem.modifyUser.date)}
                            </div>
                            <div className="imTextContainer">{props.replyItem.getText(props.user.userCode)}</div>
                            <div className="actionIconContainer" title={Translations.Remove} onClick={props.onRemoveReply}><span className="icon removeThin"></span></div>
                        </div>
                    }
                </div>
                <div className="input-group">
                    <textarea className="form-control instantMessageContent" style={{ visibility: "hidden", position: "absolute" }} ref={(elem) => { this.inputHiddenElement = elem; }}></textarea>
                    <span className="input-group-prepend">
                        <ToolAddFileButton
                            tooltip={Translations.AddAttachment}
                            fileInputName="attachments"
                            classes="attachment"
                            disabled={props.channelIsReadOnly}
                            onChange={this.handleAddAttachment}
                        />
                        {this.hasCameras() &&
                            <ToolButton
                                tooltip={Translations.AddPhoto}
                                classes="camera black"
                                onClick={this.handleAttachmentAddPhoto}
                            />
                        }
                    </span>
                    <textarea
                        className="form-control instantMessageContent"
                        placeholder={!props.channelIsReadOnly ? Translations.WriteMessage : Translations.ThisChannelHasBeenArchived}
                        value={state.content}
                        onChange={this.handleChange}
                        readOnly={props.channelIsReadOnly}
                        disabled={props.channelIsReadOnly}
                        autoFocus={false}
                        onPaste={this.handlePasteAttachments}
                        onDrop={props.onDropAttachments}
                        onDragOver={props.onDragOverAttachments}
                        onDragEnter={props.onDragEnterAttachments}
                        onDragLeave={props.onDragLeaveAttachments}
                        ref={(elem) => { this.inputElement = elem; }}
                        onInput={this.handleTextAreaInput}
                    />
                    <span className="input-group-append">
                        <ToolButton
                            tooltip={Translations.Send}
                            classes="send"
                            disabled={props.channelIsReadOnly || this.getSendingDisabled()}
                            onClick={this.saveInstantMessage}
                        />
                    </span>
                </div>
            </div>
        );
    }
}

//ChannelInstantMessagesListMenu
interface IChannelInstantMessagesListMenuProp {
    channelIsReadOnly: boolean;
    userCode: string;
    userIsChannelAdmin: boolean;
    item: IInstantMessageListItem;
    onReply: () => void;
    onClose: () => void;
}

class ChannelInstantMessagesListMenu extends React.Component<IChannelInstantMessagesListMenuProp, {}> {
    handleCancelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onClose();
    };

    handleDeleteClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const props = this.props;
        props.onClose();
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRemoveThisInstantMessage) === ConfirmationDialogResult.Yes) {
            AppUtils.callService(() => instantMessageService.removeInstantMessage(props.item.id));
        }
    };

    handleRestoreClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const props = this.props;
        props.onClose();
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRestoreThisInstantMessage) === ConfirmationDialogResult.Yes) {
            AppUtils.callService(() => instantMessageService.restoreInstantMessage(props.item.id));
        }
    };

    handleReplyClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const props = this.props;
        props.onReply();
    };
        
    render() {
        const props = this.props;
        const item = props.item;
        if (!item) return null;
        const isDeleted = item.isDeleted();
        const isMyMessage = props.userCode === item.modifyUser.code;
        return (
            <div className="listPopupMenu" onClick={this.handleCancelClick}>
                <div className="menu">
                    <div className="line" onClick={this.handleReplyClick}>{Translations.ReplyToInstantMessage}</div>
                    {(!isDeleted && (isMyMessage || props.userIsChannelAdmin) && !props.channelIsReadOnly) &&
                        <div className="line" onClick={this.handleDeleteClick}>{Translations.Remove}</div>
                    }
                    {(isDeleted && (props.userCode === item.deleteUser.code || props.userIsChannelAdmin) && !props.channelIsReadOnly) &&
                        <div className="line" onClick={this.handleRestoreClick}>{Translations.Restore}</div>
                    }
                    <div className="line" onClick={this.handleCancelClick}>{Translations.Cancel}</div>
                </div>
            </div>
        );
    }
}

// ChannelInstantMessages
const instantMessageListPageSize = 20;

export interface IChannelInstantMessagesProp {
    returnUrl: string;
    user: IUserState;
    companyId: string;
    channelId: string;
    userCode: string;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    filterActive: boolean;
    onInstantMessageUpdated: (item: IInstantMessageListItem) => void;
    onInstantMessageChannelRemoved: (id: string) => void;
    onInstantMessageChannelUpdated: (item: IInstantMessageChannelItem) => void;
}

interface IChannelInstantMessagesState {
    channel: IInstantMessageChannelItem;
    hasMore: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    selectedId: string;
    checkedIds: string[];
    items: IInstantMessageListItem[];
    replyItem: IInstantMessageListItem;
    maxInstantMessageAttachmentSize: number;
    showListMenu: boolean;
    listMenuItem: IInstantMessageListItem;
}

export class ChannelInstantMessages extends React.Component<IChannelInstantMessagesProp, IChannelInstantMessagesState> {
    private containerDiv: HTMLDivElement;
    private listDiv: HTMLDivElement;
    private toolbarDiv: HTMLDivElement;
    private signalRConnectionManager: SignalRConnectionManager;
    private readReceiptProcessingIds: string[] = [];
    private readReceiptProcessedIds: string[] = [];
    private observer: IntersectionObserver;
    private channelInstantMessageNewRef = React.createRef<ChannelInstantMessageNew>();
    private newMessageRowCount = 1;
    private replyContainerHeight = 0;

    intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.75) continue;
            const line = entry.target as HTMLDivElement;
            if (!line) continue;
            const id = line.getAttribute("data-id");
            if (!id) return;
            const item = this.state.items.find(i => i.id === id);
            if (!item || item.isUserRead()) continue;
            const processing = this.readReceiptProcessingIds.indexOf(id) > -1;
            if (processing) continue;
            const processed = this.readReceiptProcessedIds.indexOf(id) > -1;
            if (processed) continue;
            this.readReceiptProcessingIds.push(id);
            instantMessageService.sendInstantMessageReadReceipts([id])
                .then(result => {
                    if (result) {
                        this.readReceiptProcessedIds.push(id);
                    }
                })
                .finally(() => {
                    this.readReceiptProcessingIds = this.readReceiptProcessingIds.filter(i => i !== id);
                });
        }
    };

    createObserver = () => {
        this.observer = new IntersectionObserver(this.intersectionCallback, {
            root: this.containerDiv,
            rootMargin: "0px",
            threshold: 0.9
        });
    };

    removeObserver = () => {
        if (!this.observer) return;
        this.observer.disconnect();
    };

    constructor(props: IChannelInstantMessagesProp) {
        super(props);
        this.state = {
            channel: null,
            hasMore: true,
            isLoading: false,
            pageSize: instantMessageListPageSize,
            page: 1,
            selectedId: null,
            checkedIds: [],
            items: [],
            maxInstantMessageAttachmentSize: 0,
            showListMenu: false,
            listMenuItem: null,
            replyItem: null
        };
    }

    getIsFilteredList = (): boolean => {
        return !!this.props.filter;
    };

    scrollToBottom = () => {
        this.containerDiv.scrollTo({
            top: this.containerDiv.scrollHeight,
            left: 0,
            behavior: "auto"
        });
    };

    scrollToId = (id: string) => {
        if (!id) return;
        const elements = this.containerDiv.getElementsByClassName("id" + id);
        if (elements.length > 0 && !Base.isNullOrUndefined(elements[0])) {
            const y = Math.max((elements[0] as HTMLDivElement).offsetTop - 100, 0);
            this.containerDiv.scrollTo({
                top: y,
                left: 0,
                behavior: "auto"
            });
        }
    };

    addTitlesToList = (items: IInstantMessageListItem[]): IInstantMessageListItem[] => {
        const result: IInstantMessageListItem[] = [];
        const listItems = items.filter(i => !i.isTitle);
        if (listItems.length < 1) return result;
        const yesterdayDayStr = Base.timeToDateStr(new Date().addDays(-1).getTime());
        const todayDayStr = Base.timeToDateStr(new Date().getTime());
        let currentDayStr = todayDayStr;
        for (let i = listItems.length - 1; i >= 0; i --) {
            const item = listItems[i];
            const itemDayStr = Base.timeToDateStr(item.modifyUser.date);
            if (currentDayStr !== itemDayStr) {
                result.unshift(InstantMessageListItem.createTitleItem(itemDayStr === todayDayStr
                    ? Translations.Today
                    : (itemDayStr === yesterdayDayStr
                        ? Translations.Yesterday
                        : Base.timeToWeekdayStr(item.modifyUser.date) + " " + itemDayStr)));
                currentDayStr = itemDayStr;
            }
            result.unshift(item);
        }
        return result;
    };

    searchItems = async (pageSize: number, page: number, resetItems: boolean, refreshList: boolean, scrollToBottom = true) => {
        const props = this.props;
        if (!props.companyId || !props.channelId) return;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => instantMessageService.getInstantMessageListItems(props.companyId, props.channelId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1,
            props.filter, props.sortColumn, props.sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = Base.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        this.setState({
            channel: result.channel,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            items: this.addTitlesToList(items.items),
            maxInstantMessageAttachmentSize: result.maxInstantMessageAttachmentSize,
            selectedId: items.selectedId,
            checkedIds: items.checkedIds,
        }, () => {
            if (scrollToBottom) {
                this.scrollToBottom();
            } else {
                this.scrollToId(result.items.length > 0 ? result.items[0].id : "");
            }
        });
    };

    handleSearchMessages = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, !nextPage, false, false);
    };

    handleScrollSub = Base.debounce((obj: ChannelInstantMessages) => {
        const state = obj.state;
        const props = obj.props;
        if (state.isLoading || !state.hasMore) return;
        if (obj.containerDiv.scrollTop < 5) {
            obj.handleSearchMessages(props.sortColumn, props.sortOrderIsAsc, true);
        }
    }, 100);
    
    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    updateInstantMessageItemToList = (item: IInstantMessageListItem) => {
        const props = this.props;
        if (props.onInstantMessageUpdated) {
            props.onInstantMessageUpdated(item);
        }
        if (item.channelId !== props.channelId || !!props.filter) return;
        const oldItem = this.state.items.find(i => i.id === item.id);
        const isNew = !oldItem || oldItem.isNew();
        const items = this.state.items.filter(i => i.id !== item.id);
        const readReceiptSendItems = items.filter(i => this.readReceiptProcessedIds.indexOf(i.id) > -1);
        for (const readReceiptSendItem of readReceiptSendItems) {
            readReceiptSendItem.readState = InstantMessageReadState.UserRead;
        }
        items.push(item);
        InstantMessageListItem.sortInstantMessageListItems(items, props.sortColumn, props.sortOrderIsAsc);
        this.setState({
            items: this.addTitlesToList(items),
            replyItem: null
        }, () => {
            if (!isNew) return;
            window.setTimeout(() => {
                this.scrollToBottom();
            }, 50);
        });
    };

    componentDidMount(): void {
        const state = this.state;
        this.containerDiv.addEventListener("scroll", this.handleScroll);
        //Observer
        this.createObserver();
        //Search
        this.searchItems(state.pageSize, state.page, false, false);
        //SignalR
        this.signalRConnectionManager = new SignalRConnectionManager();
        this.signalRConnectionManager.openConnection(appConfig.rootUrl, {
            onUpdateInstantMessageListLine: this.updateInstantMessageItemToList,
            onRemoveInstantMessageChannel: this.props.onInstantMessageChannelRemoved,
            onUpdateInstantMessageChannel: this.props.onInstantMessageChannelUpdated
        }, Session.getSignalRToken());
    }

    componentWillUnmount(): void {
        this.containerDiv.removeEventListener("scroll", this.handleScroll);
        //SignalR
        this.signalRConnectionManager.closeConnection();
        //Observer
        this.removeObserver();
    }

    componentDidUpdate(prevProps: IChannelInstantMessagesProp, prevState: IChannelInstantMessagesState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevChannelId = prevProps.channelId;
        const channelId = props.channelId;
        const prevFilter = prevProps.filter ? prevProps.filter : "";
        const filter = props.filter ? props.filter : "";
        const prevSortColumn = prevProps.sortColumn ? prevProps.sortColumn : "";
        const sortColumn = props.sortColumn ? props.sortColumn : "";
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc ? prevProps.sortOrderIsAsc : false;
        const sortOrderIsAsc = props.sortOrderIsAsc ? props.sortOrderIsAsc : false;
        if (companyId === prevCompanyId && prevChannelId === channelId && prevFilter === filter && prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems(state.pageSize, state.page, true, false);
    }

    // #region drag and drop
    getFileAttachment = async (blob: any, category: number): Promise<FileAttachment> => {
        const attachment = await (blob instanceof File
            ? FileAttachment.fileToFileAttachment(blob, category)
            : FileAttachment.blobToFileAttachment(blob, category));
        if (!attachment) return null;
        if (!AppUtils.validateFileAttachment(attachment, Translations.MaximumSizeOfInstantMessageAttachmentIsParameterMB, this.state.maxInstantMessageAttachmentSize)) return null;
        return attachment;
    };

    handleDropAttachments = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(false);
        const attachments: FileAttachment[] = [];
        const files: any[] = [];
        if (e.dataTransfer.items) {
            [...e.dataTransfer.items].forEach((item, i) => {
                if (item.kind === "file") {
                    files.push(item.getAsFile());
                }
            });
        } else {
            [...e.dataTransfer.files].forEach((file, i) => {
                files.push(file);
            });
        }
        if (files.length < 1) return;
        for (const file of files) {
            const attachment = await this.getFileAttachment(file, 0);
            if (!attachment) return;
            attachments.push(attachment);
        }
        if (attachments.length < 1 || !AppUtils.validateFileAttachments(attachments)) return;
        if (!this.channelInstantMessageNewRef || !this.channelInstantMessageNewRef.current) return;
        this.channelInstantMessageNewRef.current.handleAddAttachments(attachments);
    };
    
    handleDragEnterAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        window.setTimeout(() => {
            this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(true);
        }, 100);
    };
    
    handleDragLeaveAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        this.channelInstantMessageNewRef.current.handleSetAttachmentDropZone(false);
    };

    handleDragOverAttachments = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };
    // #endregion drag and drop
    
    handleInputRowCountChanged = (rowCount: number) => {
        const amount = (24 * (rowCount - 1)) + this.replyContainerHeight;
        if (rowCount <= 1 && this.replyContainerHeight < 1) {
            this.newMessageRowCount = 1;
            if (this.containerDiv) {
                this.containerDiv.style.removeProperty("height");
            }
            if (this.toolbarDiv) {
                this.toolbarDiv.style.removeProperty("height");
            }
        } else {
            this.newMessageRowCount = rowCount;
            if (this.containerDiv) {
                this.containerDiv.style.setProperty("height", "calc(100% - " + amount.toString(10) + "px)");
            }
            if (this.toolbarDiv) {
                this.toolbarDiv.style.setProperty("height", (50 + amount).toString(10) + "px");
            }
        }
    };
    
    handleReplyContainerSizeChanged = (height: number) => {
        //console.log("replyContainerHeight, newMessageRowCount", height, this.newMessageRowCount)
        this.replyContainerHeight = height;
        this.handleInputRowCountChanged(this.newMessageRowCount);
    };
        
    handleSetSelected = (id: string) => {
        if (!id) return;
        this.setState({
            selectedId: id
        });
    };

    // #region ListMenu
    handleShowListMenu = (id: string) => {
        const item = this.state.items.find(i => i.id === id);
        if (!item) return;
        this.setState({
            selectedId: id,
            showListMenu: true,
            listMenuItem: item
        });
    };
    
    handleListMenuCancel = () => {
        this.setState({
            showListMenu: false
        });
    };
    
    handleListMenuReply = () => {
        this.setState({
            showListMenu: false,
            replyItem: this.state.listMenuItem
        });
    };
    // #endregion ListMenu

    handleRemoveReply = () => {
        this.setState({
            replyItem: null
        });
    };

    handleChannelEdit = () => {
        customHistory.push("/instantmessagechanneledit/" + this.props.channelId);
    };

    handleChannelExit = async () => {
        const props = this.props;
        if (!props.channelId) return;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToExitInstantMessageChannel) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => instantMessageChannelService.exitInstantMessageChannel(props.channelId, props.companyId));
            if (!result) return;
            customHistory.push("/instantmessage");
        }
    };
    
    closeView = (): void => {
        const props = this.props;
        const returnUrl = !Base.isNullOrEmpty(props.returnUrl) ? props.returnUrl : "/instantmessage";
        customHistory.push(returnUrl);
    };
    
    handleBackClick = async () => {
        this.closeView();
    };
    
    render() {
        const props = this.props;
        const state = this.state;
        return (
            <div>
                <div className="pageContentContainer">
                    <div className="instantMessageContainer mainListContainer" ref={(elem) => { this.containerDiv = elem; }}>
                        <div className="dataContainer" ref={(elem) => { this.listDiv = elem; }}>
                            {(state.channel
                                ? state.items.map((item) =>
                                    <ListLine
                                        key={item.id}
                                        id={item.id}
                                        onClick={this.handleSetSelected}
                                        onLongPress={null}
                                    >
                                        <InstantMessageListLine
                                            observer={this.observer}
                                            channelId={props.channelId}
                                            channelIsReadOnly={!state.channel.active}
                                            userCode={props.userCode}
                                            userIsChannelAdmin={state.channel.userIsAdmin}
                                            selectedId={state.selectedId}
                                            item={item}
                                            onOpenMenu={() => this.handleShowListMenu(item.id)}
                                        />
                                    </ListLine>
                                ).reverse()
                                : null
                            )}
                        </div>
                    </div>
                </div>
                <div className="toolbar newInstantMessageContainer" ref={(elem) => { this.toolbarDiv = elem; }}>
                    {!!state.channel?.active && !props.filterActive &&
                        <ChannelInstantMessageNew
                            ref={this.channelInstantMessageNewRef}
                            maxInstantMessageAttachmentSize={state.maxInstantMessageAttachmentSize}
                            user={props.user}
                            companyId={props.companyId}
                            channelId={props.channelId}
                            channelIsReadOnly={!state.channel.active}
                            replyItem={state.replyItem}
                            onInstantMessageSaved={this.updateInstantMessageItemToList}
                            onDropAttachments={this.handleDropAttachments}
                            onDragOverAttachments={this.handleDragOverAttachments}
                            onDragEnterAttachments={this.handleDragEnterAttachments}
                            onDragLeaveAttachments={this.handleDragLeaveAttachments}
                            onInputRowCountChanged={this.handleInputRowCountChanged}
                            onReplyContainerSizeChanged={this.handleReplyContainerSizeChanged}
                            onRemoveReply={this.handleRemoveReply}
                            getFileAttachment={this.getFileAttachment}
                        />
                    }
                </div>
                {state.showListMenu &&
                    <ChannelInstantMessagesListMenu
                        channelIsReadOnly={!state.channel.active}
                        userCode={props.user.userCode}
                        userIsChannelAdmin={state.channel.userIsAdmin}
                        item={state.listMenuItem}
                        onReply={this.handleListMenuReply}
                        onClose={this.handleListMenuCancel}
                    />
                }
                <EditPageHeader
                    title={Translations.InstantMessageChannel + (state.channel ? " - " + state.channel.code : "")}
                    onBackClick={this.handleBackClick}
                    appendElement={
                        <div className="buttons">
                            <ToolButtonDropdown
                                actions={[
                                    { title: Translations.InstantMessageChannelInformation, onClick: this.handleChannelEdit },
                                    { title: Translations.ExitInstantMessageChannel, onClick: this.handleChannelExit, disabled: state.channel?.userIsOnlyAdmin ?? false }
                                ]}
                                tooltip={Translations.AdditionalFunctions}
                                buttonClasses="threeDots white round mono"
                                menuRight={true}
                            />
                        </div>
                    }
                />
            </div>
        );
    }
}

// ChannelInstantMessageList
export interface IChannelInstantMessageListOwnProps {
}

export interface IChannelInstantMessageListStateProps {
    user: IUserState;
    returnUrl: string;
}

export interface IChannelInstantMessageListDispatchProps {
}

export interface IChannelInstantMessageListPathProps {
    channelId: string;
}

type ChannelInstantMessageListProp = IChannelInstantMessageListOwnProps & IChannelInstantMessageListStateProps & IChannelInstantMessageListDispatchProps & RouteComponentProps<IChannelInstantMessageListPathProps>;

interface IChannelInstantMessageListState {
    sortColumn: string;
    sortOrderIsAsc: boolean;
    filter: string;
    filterActive: boolean;
}

export class ChannelInstantMessageList extends React.Component<ChannelInstantMessageListProp, IChannelInstantMessageListState> {
    constructor(props: ChannelInstantMessageListProp) {
        super(props);
        this.state = {
            sortColumn: "modifyDate",
            sortOrderIsAsc: false,
            filter: "",
            filterActive: false
        };
    }

    componentDidUpdate(prevProps: ChannelInstantMessageListProp, prevState: IChannelInstantMessageListState) {
        const props = this.props;
        const prevChannelId = prevProps.match.params.channelId ?? "";
        const channelId = props.match.params.channelId ?? "";
        if (prevChannelId === channelId) return;
        this.setState({
            sortColumn: "modifyDate",
            sortOrderIsAsc: false,
            filter: "",
            filterActive: false
        });
    }

    changeFilter = (filter: string) => {
        this.setState({
            filter,
            filterActive: !!filter
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <ChannelInstantMessages
                returnUrl={props.returnUrl}
                user={props.user}
                companyId={props.user.companyId}
                channelId={props.match.params.channelId ?? ""}
                userCode={props.user.userCode}
                filter={state.filter}
                filterActive={state.filterActive}
                sortColumn={state.sortColumn}
                sortOrderIsAsc={state.sortOrderIsAsc}
                onInstantMessageUpdated={null}
                onInstantMessageChannelRemoved={null}
                onInstantMessageChannelUpdated={null}
            />
        );
    }
}